import { useCallback, useState } from "react";

import * as api from "../api/client";
import { MetricCard } from "../components/MetricCard";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";
import type { MailTriageRecord, MailTriageUrgency } from "../types/api";
import { MAIL_TRIAGE_CATEGORIES } from "../types/api";

type TriageSortKey =
  | "sender"
  | "subject"
  | "category"
  | "urgency"
  | "classifier"
  | "receivedAt"
  | "labelApplied";
const TRIAGE_ACCESSORS: Record<TriageSortKey, (r: MailTriageRecord) => string | number | null> = {
  sender: (r) => r.sender,
  subject: (r) => r.subject,
  category: (r) => r.category,
  urgency: (r) => r.urgency,
  classifier: (r) => r.classifier,
  receivedAt: (r) => r.receivedAt.getTime(),
  labelApplied: (r) => r.labelApplied ?? null,
};

// DMARC/SPF alignment realistically never sits at a literal 100% (mailing-list
// forwarding and similar legitimate re-send paths produce occasional soft-fails), so
// warning on anything short of perfect produced constant false-positive alarms (PK
// 2026-07-21 SPF/DKIM diagnosis: a genuinely healthy ~93.8% SPF pass rate read as
// broken). 90% is the threshold below which a real degradation is likely, not routine
// noise — below this, investigate; above it, the outbound-auth pipeline is healthy.
const DMARC_PASS_RATE_WARN_THRESHOLD = 0.9;

const TD = "px-4 py-2.5 text-sm text-slate-900";

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatDate(value: Date): string {
  return Number.isNaN(value.getTime()) ? "—" : value.toLocaleString();
}

function MailTriageTable({
  records,
  sort,
  onSort,
}: {
  records: MailTriageRecord[];
  sort: ReturnType<typeof useSortableRows<MailTriageRecord, TriageSortKey>>["sort"];
  onSort: (key: TriageSortKey) => void;
}) {
  if (records.length === 0) {
    return <div className="text-slate-500 text-sm py-6 text-center">No matching records</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableTh label="Sender" sortKey="sender" sort={sort} onSort={onSort} sticky />
              <SortableTh label="Subject" sortKey="subject" sort={sort} onSort={onSort} />
              <SortableTh label="Category" sortKey="category" sort={sort} onSort={onSort} />
              <SortableTh label="Urgency" sortKey="urgency" sort={sort} onSort={onSort} />
              <SortableTh label="Classifier" sortKey="classifier" sort={sort} onSort={onSort} />
              <SortableTh label="Received" sortKey="receivedAt" sort={sort} onSort={onSort} />
              <SortableTh
                label="Label Applied"
                sortKey="labelApplied"
                sort={sort}
                onSort={onSort}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {records.map((r) => (
              <tr key={r.messageId} className="group hover:bg-slate-50 transition-colors">
                <td className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50`}>
                  {r.sender}
                </td>
                <td className={`${TD} max-w-[320px] truncate`} title={r.subject}>
                  {r.subject}
                </td>
                <td className={TD}>{r.category}</td>
                <td className={TD}>{r.urgency}</td>
                <td className={TD}>{r.classifier}</td>
                <td className="px-4 py-2.5 text-sm text-slate-600">{formatDate(r.receivedAt)}</td>
                <td className={TD}>{r.labelApplied ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterBar({
  category,
  urgency,
  onCategoryChange,
  onUrgencyChange,
}: {
  category: string;
  urgency: string;
  onCategoryChange: (value: string) => void;
  onUrgencyChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-4">
      <label className="flex items-center gap-2 text-sm text-slate-600">
        Category
        <select
          aria-label="Category"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          <option value="">All categories</option>
          {MAIL_TRIAGE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        Urgency
        <select
          aria-label="Urgency"
          value={urgency}
          onChange={(e) => onUrgencyChange(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          <option value="">All urgencies</option>
          <option value="urgent">urgent</option>
          <option value="normal">normal</option>
          <option value="ignore">ignore</option>
        </select>
      </label>
    </div>
  );
}

export function MailTriagePage() {
  const dmarc = useApi(() => api.getDmarcSummary(), []);

  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("");

  // Cursor-paginated at the wire level, but — same reasoning as UsersPage —
  // a paginated table cannot support honest column sorting, so this loads
  // the full filtered set via listAllMailTriageRecords and sorts that
  // client-side. No fake Prev/Next controls (PK 2026-07-25 directive).
  const list = useApi(
    () =>
      api.listAllMailTriageRecords({
        category: category || undefined,
        urgency: (urgency || undefined) as MailTriageUrgency | undefined,
      }),
    [category, urgency]
  );

  const triageSort = useSortableRows(list.data?.records, TRIAGE_ACCESSORS, {
    key: "receivedAt",
    direction: "desc",
  });

  const handleCategoryChange = useCallback((value: string) => {
    setCategory(value);
  }, []);

  const handleUrgencyChange = useCallback((value: string) => {
    setUrgency(value);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Mail Triage</h2>
        <p className="text-sm text-slate-500 mt-1">
          Incoming-mail classification and DMARC report health.
        </p>
      </div>

      <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">DMARC Summary</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Based on Google DMARC aggregate reports for outbound mail sent from our domains (sender
            authentication health) — not inbound spam volume.
          </p>
        </div>
        {dmarc.loading && (
          <div className="text-slate-500 text-sm py-6 text-center">Loading DMARC summary…</div>
        )}
        {!dmarc.loading && dmarc.error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" role="alert">
            Error: {dmarc.error}
          </div>
        )}
        {dmarc.data && !dmarc.loading && (
          <>
            {/* DMARC Pass Rate is the headline metric (RFC 7489: spf_pass OR dkim_pass
                per report) — a viewer should not have to mentally combine the SPF/DKIM
                breakdown to know whether outbound mail is authenticating cleanly. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <MetricCard
                label="DMARC Pass Rate"
                value={formatPercent(dmarc.data.dmarcPassRate)}
                warn={dmarc.data.dmarcPassRate < DMARC_PASS_RATE_WARN_THRESHOLD}
              />
              <MetricCard label="Reports" value={dmarc.data.reportCount} />
              <MetricCard label="Messages" value={dmarc.data.totalMessages} />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mt-4 mb-2">
                Breakdown
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MetricCard
                  label="SPF Pass Rate"
                  value={formatPercent(dmarc.data.spfPassRate)}
                  warn={dmarc.data.spfPassRate < DMARC_PASS_RATE_WARN_THRESHOLD}
                />
                <MetricCard
                  label="DKIM Pass Rate"
                  value={formatPercent(dmarc.data.dkimPassRate)}
                  warn={dmarc.data.dkimPassRate < DMARC_PASS_RATE_WARN_THRESHOLD}
                />
              </div>
            </div>
          </>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-slate-900">Records</h3>
          <FilterBar
            category={category}
            urgency={urgency}
            onCategoryChange={handleCategoryChange}
            onUrgencyChange={handleUrgencyChange}
          />
        </div>

        {list.loading && (
          <div className="text-slate-500 text-sm py-6 text-center">
            Loading mail-triage records…
          </div>
        )}

        {!list.loading && list.error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" role="alert">
            Error: {list.error}
          </div>
        )}

        {list.data?.truncated && (
          <div
            className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4"
            role="alert"
          >
            <span className="text-2xl">⚠️</span>
            <div>
              <div className="text-amber-800 font-semibold">Record list truncated</div>
              <div className="text-amber-700 text-sm">
                The mail-triage collection hit the fetch page cap for these filters — the table
                below is a partial view, not the full matching set.
              </div>
            </div>
          </div>
        )}

        {list.data && !list.loading && (
          <>
            <p className="text-xs text-slate-400">{list.data.records.length} matching record(s)</p>
            <MailTriageTable
              records={triageSort.sorted}
              sort={triageSort.sort}
              onSort={triageSort.toggleSort}
            />
          </>
        )}
      </section>
    </div>
  );
}
